import React, { useState, useMemo } from 'react';
import { COLLECTIONS } from '../data/mock';
import { useCatalog } from '../context/CatalogContext';
import { useTranslation } from '../hooks/useTranslation';
import ProductCard from '../components/ProductCard';

export default function AllWatches() {
  const { products, loading } = useCatalog();
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('featured');

  const items = useMemo(() => {
    let list = filter === 'all' ? products : products.filter((p) => p.collection === filter);
    list = [...list];
    if (sort === 'asc') list.sort((a, b) => a.price - b.price);
    if (sort === 'desc') list.sort((a, b) => b.price - a.price);
    return list;
  }, [filter, sort, products]);

  const categories = [{ slug: 'all', name: t('allwatches.all') }, ...COLLECTIONS.map((c) => ({ slug: c.slug, name: c.name }))];

  return (
    <main>
      {/* Compact header */}
      <section className="pt-8 pb-6 md:pt-12 md:pb-10 bg-ivory text-center">
        <div className="max-w-3xl mx-auto px-4 animate-fade-up">
          <div className="text-[10px] md:text-[11px] tracking-luxury uppercase text-gold mb-3">{t('allwatches.label')}</div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light">{t('allwatches.title')}</h1>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          {/* Category filters — horizontal scroll, 3 visible on mobile, full width */}
          <div className="border-y border-[#EAE4D8] mb-8 md:mb-12">
            {/* Mobile: horizontal scroll, 3 categories visible at a time */}
            <div className="md:hidden -mx-4">
              <div className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar">
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => setFilter(c.slug)}
                    data-testid={`allwatches-filter-${c.slug}`}
                    className={`snap-start shrink-0 basis-1/3 px-2 py-4 text-center text-[10px] tracking-[0.22em] uppercase transition-colors ${
                      filter === c.slug ? 'text-black font-medium' : 'text-[#888] hover:text-black'
                    }`}
                  >
                    <span className="inline-block relative pb-1.5">
                      {c.name}
                      {filter === c.slug && <span className="absolute left-0 right-0 -bottom-0 h-[1.5px] bg-black" />}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            {/* Desktop: full width flex spread */}
            <div className="hidden md:flex items-center justify-between py-4 gap-2">
              <div className="flex flex-1 items-center justify-between">
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    onClick={() => setFilter(c.slug)}
                    data-testid={`allwatches-filter-desktop-${c.slug}`}
                    className={`text-[11px] tracking-[0.28em] uppercase py-2 transition-colors relative ${
                      filter === c.slug ? 'text-black font-medium' : 'text-[#888] hover:text-black'
                    }`}
                  >
                    <span className="relative pb-1.5">
                      {c.name}
                      {filter === c.slug && <span className="absolute left-0 right-0 -bottom-0 h-[1.5px] bg-black" />}
                    </span>
                  </button>
                ))}
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="allwatches-sort-select-desktop"
                className="bg-transparent border border-[#EAE4D8] px-4 py-2 text-[11px] tracking-luxury uppercase focus:outline-none focus:border-black ml-6"
              >
                <option value="featured">{t('allwatches.sortFeatured')}</option>
                <option value="asc">{t('allwatches.sortAsc')}</option>
                <option value="desc">{t('allwatches.sortDesc')}</option>
              </select>
            </div>
          </div>

          {/* Mobile sort (below scroll categories) */}
          <div className="md:hidden mb-6 flex justify-end">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="allwatches-sort-select-mobile"
              className="bg-transparent border border-[#EAE4D8] px-4 py-2 text-[10px] tracking-luxury uppercase focus:outline-none focus:border-black"
            >
              <option value="featured">{t('allwatches.sortFeatured')}</option>
              <option value="asc">{t('allwatches.sortAsc')}</option>
              <option value="desc">{t('allwatches.sortDesc')}</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-16 text-[#666]">{t('allwatches.loading')}</div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-[#666]">
              {t('allwatches.empty')}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8 lg:gap-10">
              {items.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
